import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactSignatureCanvas from 'react-signature-canvas'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import DisclaimerTemplate, { DisclaimerTemplateProps } from '../../templates/Disclaimer'
import { useHeaderProps } from '../../components/Header/useProps'
import Header from '../../components/Header'
import { actions, selectors } from '../../redux'
import { Screen } from '../../types/global'
import { useSagaTakeEvery } from '../../hooks/useSaga'

const DisclaimerPage: React.FC = () => {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()

  // SELECTORS

  const acceptDisclaimer = useSelector(selectors.app.acceptDisclaimer)
  const getDisclaimers = useSelector(selectors.app.getDisclaimers)

  const disclaimer = useMemo(() => {
    switch (i18n.language) {
      case 'fr':
        return getDisclaimers.data?.disclaimerFr
      case 'en':
        return getDisclaimers.data?.disclaimerEn
    }
  }, [getDisclaimers.data?.disclaimerEn, getDisclaimers.data?.disclaimerFr, i18n.language])

  // REF

  const $signatureCanvasRef = useRef<ReactSignatureCanvas | null>(null)

  // STATE

  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)

  // FUNCTION

  const checkCanSubmit = useCallback(() => {
    setIsSubmitEnabled(!$signatureCanvasRef.current?.isEmpty() && isCheckboxChecked)
  }, [isCheckboxChecked])

  // PROPS

  const headerProps = useHeaderProps()

  const templateProps: DisclaimerTemplateProps = useMemo(() => {
    return {
      layoutProps: {
        header: <Header {...headerProps} />,
      },
      title: t('disclaimer.title'),
      disclaimer: {
        text: disclaimer ?? '',
      },
      checkbox: {
        label: t('disclaimer.checkbox.label'),
        input: {
          checked: isCheckboxChecked,
          onChange: (e) => {
            setIsCheckboxChecked(e.target.checked)
          },
        },
      },
      signatureLabel: t('disclaimer.signature.label'),
      signatureCanvas: {
        indicationText: t('disclaimer.signature.indication'),
        onRef: (ref) => {
          $signatureCanvasRef.current = ref
        },
        onClear: () => {
          setIsSubmitEnabled(false)
        },
        reactSignatureCanvas: {
          onEnd: () => {
            checkCanSubmit()
          },
        },
      },
      // back: {
      //   label: t('disclaimer.back'),
      //   onClick: () => {
      //     dispatch(actions.app.setScreen({ screen: Screen.appointement }))
      //   },
      // },
      confirmButton: {
        text: t('disclaimer.submitButton'),
        disabled: !isSubmitEnabled || acceptDisclaimer.pending,
        onClick: () => {
          dispatch(
            actions.app.acceptDisclaimerRequest({
              signature: $signatureCanvasRef.current!.toDataURL(),
            })
          )
        },
      },
    }
  }, [
    acceptDisclaimer.pending,
    checkCanSubmit,
    disclaimer,
    dispatch,
    headerProps,
    isCheckboxChecked,
    isSubmitEnabled,
    t,
  ])

  // EFFECT

  useEffect(() => {
    checkCanSubmit()
  }, [checkCanSubmit])

  // SAGA

  useSagaTakeEvery(
    actions.app.acceptDisclaimerSuccess,
    useCallback(() => {
      dispatch(actions.app.setScreen({ screen: Screen.message }))
    }, [dispatch])
  )

  // RETURN

  return <DisclaimerTemplate {...templateProps} />
}

export default DisclaimerPage
